import { ChevronLeft, ChevronRight } from 'lucide-react' 

export interface MetricPeriod {
  period_id: number
  period_start: string
  period_end: string
  period_name?: string
  month_number?: number
  year_number?: number
  period_len_days?: number
}

export interface PeriodSelectorProps {
  currentPeriod: MetricPeriod | null
  onPrevPeriod: () => void
  onNextPeriod: () => void
  canGoPrev: boolean
  canGoNext: boolean
  showViewModeSelector?: boolean
  isCurrentPeriod: boolean
  viewMode?: 'period' | 'current_week'
  onViewModeChange?: (mode: 'period' | 'current_week') => void
  periodFilterValue?: string
  periodFilterOptions?: Array<{ value: string; label: string; disabled?: boolean }>
  onPeriodFilterChange?: (value: string) => void
  selectedRange?: {
    mode: 'period' | 'week' | 'last_3_days' | 'yesterday'
    start: string
    end: string
    label: string
  } | null
  compact?: boolean
  stacked?: boolean
  showPeriodLength?: boolean
}

export function PeriodFilterSelect({ value, options, onChange, compact = false, stacked = false, label = 'Диапазон периода' }: {
  value: string
  options: Array<{ value: string; label: string; disabled?: boolean }>
  onChange: (value: string) => void
  compact?: boolean
  stacked?: boolean
  label?: string
}) {
  const className = stacked
    ? `w-full min-w-0 ${compact ? 'rounded-lg' : 'rounded-xl'} border border-border bg-card px-3 ${compact ? 'py-2' : 'py-2.5'} text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary`
    : compact
      ? 'w-full sm:w-auto px-3 py-1 text-sm border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-primary'
      : 'w-full sm:w-auto px-3 py-1.5 text-sm border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-primary'

  return (
    <select value={value} onChange={event => onChange(event.target.value)} className={className} aria-label={label}>
      {options.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
    </select>
  )
}

export function PeriodSelector({
  currentPeriod,
  onPrevPeriod,
  onNextPeriod,
  canGoPrev,
  canGoNext,
  showViewModeSelector = false,
  isCurrentPeriod,
  viewMode = 'period',
  onViewModeChange,
  periodFilterValue,
  periodFilterOptions,
  onPeriodFilterChange,
  selectedRange,
  compact = false,
  stacked = false,
  showPeriodLength = true
}: PeriodSelectorProps) {
  if (!currentPeriod?.period_start) return null

  const formatShortDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-')
    if (!year || !month || !day) return dateString
    return `${day}.${month}`
  }

  // Форматируем период с разделением на части для адаптивности
  const formatPeriodParts = () => {
    if (!currentPeriod.month_number || !currentPeriod.year_number) {
      return { main: currentPeriod.period_name || 'Период', details: null, dates: null }
    }

    const monthNames = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ]
    
    const monthName = monthNames[currentPeriod.month_number - 1] || `Месяц ${currentPeriod.month_number}`
    const start = new Date(currentPeriod.period_start)
    const end = new Date(currentPeriod.period_end)
    
    const weeks = currentPeriod.period_len_days 
      ? Math.round(currentPeriod.period_len_days / 7)
      : Math.round((end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000))
    
    const startDay = start.getDate().toString().padStart(2, '0')
    const startMonth = (start.getMonth() + 1).toString().padStart(2, '0')
    const startYear = start.getFullYear()
    const endDay = end.getDate().toString().padStart(2, '0')
    const endMonth = (end.getMonth() + 1).toString().padStart(2, '0')
    const endYear = end.getFullYear()
    
    const dateRange = startYear !== endYear 
      ? `${startDay}.${startMonth}.${startYear} - ${endDay}.${endMonth}.${endYear}`
      : `${startDay}.${startMonth} - ${endDay}.${endMonth}`
    
    const weeksText = `${weeks} ${weeks === 1 ? 'неделя' : weeks < 5 ? 'недели' : 'недель'}`

    if (selectedRange && selectedRange.mode !== 'period') {
      return {
        main: `${monthName} ${currentPeriod.year_number}`,
        details: selectedRange.label,
        dates: `${formatShortDate(selectedRange.start)} - ${formatShortDate(selectedRange.end)}`
      }
    }

    return {
      main: `${monthName} ${currentPeriod.year_number}`,
      details: showPeriodLength ? weeksText : null,
      dates: dateRange
    }
  }

  const periodParts = formatPeriodParts()
  const wrapperClass = compact ? 'flex items-center justify-between py-1.5' : 'flex items-center justify-between py-4'
  const navButtonClass = compact
    ? 'p-1.5 hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
    : 'p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
  const titleClass = compact ? 'text-sm sm:text-[15px] lg:text-base font-semibold' : 'text-sm sm:text-base lg:text-lg font-semibold'

  if (stacked) {
    const subtitle = [periodParts.details, periodParts.dates].filter(Boolean).join(' · ')
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-1">
          <button
            onClick={onPrevPeriod}
            disabled={!canGoPrev}
            className={navButtonClass}
            aria-label="Предыдущий период"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="min-w-0 text-center">
            <h2 className="truncate text-xs font-semibold">{periodParts.main}</h2>
            {subtitle ? <div className="truncate text-[10px] leading-4 text-muted-foreground">{subtitle}</div> : null}
          </div>

          <button
            onClick={onNextPeriod}
            disabled={!canGoNext}
            className={navButtonClass}
            aria-label="Следующий период"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {showViewModeSelector && periodFilterValue && onPeriodFilterChange && periodFilterOptions && (
          <PeriodFilterSelect value={periodFilterValue} options={periodFilterOptions} onChange={onPeriodFilterChange} compact={compact} stacked />
        )}

        {showViewModeSelector && !periodFilterValue && isCurrentPeriod && onViewModeChange && (
          <div className="inline-flex w-full rounded-lg bg-muted p-1">
            <button
              onClick={() => onViewModeChange('period')}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-all ${
                viewMode === 'period'
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              За период
            </button>
            <button
              onClick={() => onViewModeChange('current_week')}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-all ${
                viewMode === 'current_week'
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Эта неделя
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={wrapperClass}>
      <button
        onClick={onPrevPeriod}
        disabled={!canGoPrev}
        className={navButtonClass}
        aria-label="Предыдущий период"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
        <div className="text-center">
          <h2 className={titleClass}>
            {periodParts.main}
            {periodParts.details && (
              <span className="hidden sm:inline"> - {periodParts.details}</span>
            )}
            {periodParts.dates && (
              <span> ({periodParts.dates})</span>
            )}
          </h2>
        </div>

        {/* Unified period/week selector */}
        {showViewModeSelector && periodFilterValue && onPeriodFilterChange && periodFilterOptions && (
          <PeriodFilterSelect value={periodFilterValue} options={periodFilterOptions} onChange={onPeriodFilterChange} compact={compact} />
        )}

        {/* Legacy selector */}
        {showViewModeSelector && !periodFilterValue && isCurrentPeriod && onViewModeChange && (
          <div className="inline-flex rounded-lg bg-muted p-1">
            <button
              onClick={() => onViewModeChange('period')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                viewMode === 'period'
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              За период
            </button>
            <button
              onClick={() => onViewModeChange('current_week')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                viewMode === 'current_week'
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Эта неделя
            </button>
          </div>
        )}
      </div>

      <button
        onClick={onNextPeriod}
        disabled={!canGoNext}
        className={navButtonClass}
        aria-label="Следующий период"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  )
}
